import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Clock3, Loader2, Package2, Play } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import FlowLoader from "../FlowLoader";
import { PICKING_POLL_MS } from "./constants";

// 1-3 are the renamable named queues; 6 is Bulk Pick (fixed name, stock
// allocated from the fewest locations). All four list and start the same way.
export type SpecialQueueId = 1 | 2 | 3 | 6;

export interface SpecialQueue {
    id: SpecialQueueId;
    name: string;
    orders: number;
    items: number;
}

export interface QueueWaveStartOptions {
    tier: "low" | "high";
    ageFilter: "all" | "2+" | "1-2" | "<1" | "custom";
    deliveryFilter: "all" | "express" | "standard";
    channelFilter: string;
    ageFrom: string;
    ageTo: string;
    // Clock filter; stamped on the wave header for consistency (queue
    // waves themselves ignore every selector filter — see BE).
    dueFilter?: boolean;
}

interface QueueOrder {
    order_id: string;
    order_number: string | null;
    channel_created_at: string;
    total_items: number;
    ready: boolean;
}

interface QueueOrdersResponse {
    queue: { id: SpecialQueueId; name: string };
    orders: QueueOrder[];
}

interface Props {
    queue: Pick<SpecialQueue, "id" | "name">;
    startOptions: QueueWaveStartOptions;
    onBack: () => void;
    onWaveStarted: () => void;
}

const formatAgo = (
    raw: string,
    t: (key: string, vars?: Record<string, string>) => string,
): string => {
    const timestamp = new Date(raw).getTime();
    if (!Number.isFinite(timestamp)) return "";
    const ms = Math.max(0, Date.now() - timestamp);
    if (ms < 60_000) return t("picking_ago_just_now");
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 60) return t("picking_ago_minutes", { m: String(minutes) });
    const hours = Math.floor(minutes / 60);
    return t("picking_ago_hours", { h: String(hours) });
};

const QueueOrderList = ({ queue, startOptions, onBack, onWaveStarted }: Props) => {
    const { t } = useTranslation();
    const [queueName, setQueueName] = useState(queue.name);
    const [orders, setOrders] = useState<QueueOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [startingOrderId, setStartingOrderId] = useState<string | null>(null);
    const [startingQueue, setStartingQueue] = useState(false);
    const busy = startingQueue || startingOrderId !== null;

    const loadOrders = useCallback(async (showLoader = false) => {
        if (showLoader) setLoading(true);
        try {
            const res = await apiFetch(`/picking/queue/${queue.id}/orders`, {});
            if (!res.ok) return;
            const body: QueueOrdersResponse = await res.json();
            setQueueName(body.queue?.name || queue.name);
            setOrders(Array.isArray(body.orders) ? body.orders : []);
        } catch {
            // Transient failures are retried by the light poll below.
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [queue.id, queue.name]);

    useEffect(() => {
        let alive = true;
        const tick = async (showLoader = false) => {
            if (!alive) return;
            await loadOrders(showLoader);
        };
        void tick(true);
        const intervalId = window.setInterval(() => { void tick(); }, PICKING_POLL_MS);
        return () => {
            alive = false;
            window.clearInterval(intervalId);
        };
    }, [loadOrders]);

    const startWave = async (orderId?: string) => {
        if (busy) return;
        if (orderId) setStartingOrderId(orderId);
        else setStartingQueue(true);
        let handedOff = false;
        try {
            const res = await apiFetch("/picking/wave/start", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    pickType: "large",
                    pickQueue: queue.id,
                    ...(orderId ? { orderId } : {}),
                    tier: startOptions.tier,
                    ageFilter: startOptions.ageFilter,
                    deliveryFilter: startOptions.deliveryFilter,
                    channelFilter: startOptions.channelFilter,
                    ageFrom: startOptions.ageFrom || null,
                    ageTo: startOptions.ageTo || null,
                    dueFilter: startOptions.dueFilter === true,
                }),
            });
            if (res.ok) {
                handedOff = true;
                onWaveStarted();
                return;
            }
            const errorBody = await res.json().catch(() => ({}));
            const code = errorBody?.code as string | undefined;
            if (code === "ALREADY_ACTIVE") {
                handedOff = true;
                toast.info(t("picking_already_active_wave"), { toastId: "picking-queue-already-active" });
                onWaveStarted();
                return;
            }
            if (code === "NO_CANDIDATES" || code === "NO_MATCHING" || code === "NO_SELECTED") {
                toast.info(t("picking_no_orders_to_pick"), { toastId: "picking-queue-no-orders" });
                void loadOrders();
                return;
            }
            if (code === "BUILD_CONFLICT") {
                toast.error(t("picking_start_conflict"), { toastId: "picking-queue-conflict" });
                return;
            }
            toast.error(t("picking_start_failed"), { toastId: "picking-queue-start-fail" });
        } catch {
            toast.error(t("picking_start_failed"), { toastId: "picking-queue-start-net" });
        } finally {
            if (!handedOff) {
                setStartingOrderId(null);
                setStartingQueue(false);
            }
        }
    };

    const readyOrders = orders.filter(order => order.ready);

    return (
        <div className="picking-selector picking-queue-orders">
            <div className="page-title-bar picking-selector-header picking-queue-orders-header">
                <button
                    type="button"
                    className="page-back-btn"
                    onClick={onBack}
                    disabled={busy}
                    aria-label={t("picking_queue_back")}
                >
                    <ArrowLeft size={20} aria-hidden="true" />
                </button>
                <div className="picking-queue-orders-heading">
                    <h1 className="page-title">{queueName}</h1>
                    <span>{t("picking_special_queues_title")}</span>
                </div>
                <button
                    type="button"
                    className="btn btn-primary picking-queue-start-btn"
                    onClick={() => void startWave()}
                    disabled={busy || readyOrders.length === 0}
                >
                    {startingQueue
                        ? <Loader2 size={18} className="picking-starting-spinner" aria-hidden="true" />
                        : <Play size={18} aria-hidden="true" />}
                    {t("picking_queue_start")}
                </button>
            </div>

            {loading ? (
                <div className="picking-queue-loading"><FlowLoader /></div>
            ) : orders.length === 0 ? (
                <div className="picking-selector-empty">{t("picking_queue_empty")}</div>
            ) : (
                <ul className="picking-queue-order-list">
                    {orders.map(order => {
                        const age = formatAgo(order.channel_created_at, t);
                        const isStarting = startingOrderId === order.order_id;
                        return (
                            <li
                                key={order.order_id}
                                className={`picking-queue-order-row${order.ready ? "" : " picking-queue-order-row--not-ready"}`}
                            >
                                <div className="picking-queue-order-main">
                                    <strong>{order.order_number || order.order_id}</strong>
                                    <div className="picking-queue-order-meta">
                                        {age && <span><Clock3 size={15} aria-hidden="true" />{age}</span>}
                                        <span>
                                            <Package2 size={15} aria-hidden="true" />
                                            {t("picking_queue_items", { count: String(order.total_items) })}
                                        </span>
                                    </div>
                                    {!order.ready && (
                                        <span className="picking-queue-not-ready">{t("picking_queue_not_ready")}</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary picking-queue-pick-btn"
                                    onClick={() => void startWave(order.order_id)}
                                    disabled={busy || !order.ready}
                                >
                                    {isStarting && <Loader2 size={17} className="picking-starting-spinner" aria-hidden="true" />}
                                    {t("picking_queue_pick")}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {busy && (
                <div className="picking-building-overlay" role="status" aria-live="polite" aria-busy="true">
                    <Loader2 className="picking-starting-spinner" size={56} strokeWidth={2.25} aria-hidden="true" />
                </div>
            )}
        </div>
    );
};

export default QueueOrderList;
